import { Box, Typography, Link as MUILink, IconButton, useTheme } from '@mui/material';
import { Link } from 'react-router-dom';
import Image from './image';
import logo from '../../assets/logo.png';
import TwitterIcon from '../../assets/icons/twitter.svg';
import FaqsIcon from '../../assets/icons/Faqs.svg';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import DescriptionIcon from '@mui/icons-material/Description';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import InstagramIcon from '@mui/icons-material/Instagram';
import DiscordIcon2 from '../../icons/DiscordIcon';
import Blog from '../../assets/icons/blog.svg';

export default function Footer() {
  const theme = useTheme();
  const currentYear = new Date().getFullYear();

  const footerLinks = [
    { label: 'Blog', to: '/blog', icon: <Image src={Blog} alt="Blog Icon" width={16} height={16} /> },
    { label: 'FAQs', to: '/faq', icon: <Image src={FaqsIcon} alt="FAQ Icon" width={16} height={16} /> },
    { label: 'Docs', to: 'https://saagasai.mintlify.app', icon: <DescriptionIcon />, external: true },
    { label: 'Contact Us', to: 'mailto:support@saagas.ai', icon: <MailOutlineIcon />, external: true },
    // { label: 'Discord', to: 'https://t.co/401XPwdXQx', icon: <DiscordIcon2 style={{ width: 20, height: 20 }} />, external: true }
  ];

  const policyLinks = [
    { label: 'Terms & Conditions', to: '/terms-of-use' },
    { label: 'Privacy Policy', to: '/privacy-policy' }
  ];

  const socialLinks = [
    {
      label: 'X profile',
      href: 'https://x.com/saagasinfluencr',
      icon: <Image src={TwitterIcon} alt="X Icon" width={16} height={20} />
    },
    {
      label: 'Discord server',
      href: 'https://t.co/401XPwdXQx',
      icon: <DiscordIcon2 style={{ width: 20, height: 20 }} />
    },
    {
      label: 'LinkedIn',
      href: 'https://www.linkedin.com/company/saagasai/',
      icon: <LinkedInIcon style={{ width: 20, height: 20 }} />
    },
    {
      label: 'Instagram',
      href: 'https://www.instagram.com/saagas.ugc/',
      icon: <InstagramIcon style={{ width: 20, height: 20 }} />
    }
  ];

  return (
    <Box
      component="footer"
      sx={{
        px: { xs: 2, md: 5 },
        py: 5,
        maxWidth: '1440px',
        m: 'auto',
        backgroundColor: '#000000'
      }}
    >
      {/* Mobile view structure */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 5,
          margin: 'auto',
          maxWidth: '1260px',

          // Desktop view
          [theme.breakpoints.up('md')]: {
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 0
          }
        }}
      >
        {/* Logo */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
            mb: { xs: 2, md: 0 },

            [theme.breakpoints.up('md')]: {
              width: 'auto',
              justifyContent: 'flex-start'
            }
          }}
        >
          <Link to="/">
            <Image src={logo} alt="Saagas.ai logo" width={42} height={42} />
          </Link>
        </Box>

        {/* Navigation Links - Each on its own line for mobile */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            gap: 4,

            [theme.breakpoints.up('md')]: {
              flexDirection: 'row',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: 3
            }
          }}
        >
          {/* Split Footer Links */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 5,

              [theme.breakpoints.up('md')]: {
                flexDirection: 'row',
                justifyContent: 'center',
                flexWrap: 'wrap',
                gap: 3
              }
            }}
          >
            {/* First 3 Links in Row */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: 3,
                ml: { xs: 0, md: 0, lg: 10 },
              }}
            >
              {footerLinks.slice(0, 3).map(({ label, to, icon, external }) => (
                <MUILink
                  key={label}
                  component={external ? 'a' : Link}
                  href={external ? to : undefined}
                  to={!external ? to : undefined}
                  underline="none"
                  target={external ? '_blank' : undefined}
                  rel={external ? 'noopener noreferrer' : undefined}
                  sx={{
                    fontSize: '16px',
                    color: '#FFFFFF',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5
                  }}
                >
                  {icon}
                  {label}
                </MUILink>
              ))}
            </Box>

            {/* Contact Us and Discord - stacked below */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 4,
                [theme.breakpoints.up('md')]: {
                  flexDirection: 'row'
                }
              }}
            >
              {footerLinks.slice(3).map(({ label, to, icon, external }) => (
                <MUILink
                  key={label}
                  component={external ? 'a' : Link}
                  href={external ? to : undefined}
                  to={!external ? to : undefined}
                  underline="none"
                  target={external ? '_blank' : undefined}
                  rel={external ? 'noopener noreferrer' : undefined}
                  sx={{
                    fontSize: '16px',
                    color: '#FFFFFF',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5
                  }}
                >
                  {icon}
                  {label}
                </MUILink>
              ))}
            </Box>
          </Box>
        </Box>

        {/* Social Media Icons */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 3,
            [theme.breakpoints.up('md')]: {
              justifyContent: 'flex-end'
            }
          }}
        >
          {socialLinks.map(({ label, href, icon }) => (
            <IconButton key={label} component={MUILink} href={href} target="_blank" aria-label={label} sx={{ color: '#FFFFFF', width: 30, height: 30 }}>
              {icon}
            </IconButton>
          ))}
        </Box>
      </Box>

      {/* Bottom Section with Copyright and Policies */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          margin: 'auto',
          maxWidth: '1260px',
          borderTop: '1px solid #18181B',
          pt: 4,

          [theme.breakpoints.up('md')]: {
            flexDirection: 'row',
            justifyContent: 'space-between',
            gap: 0
          }
        }}
      >
        {/* Copyright Text */}
        <Typography
          variant="body2"
          sx={{
            color: '#A8A8A8',
            order: { xs: 2, md: 1 },
            mt: { xs: 2, md: 0 }
          }}
        >
          © Copyright {currentYear}, All Rights Reserved
        </Typography>

        {/* Policy Links */}
        <Box
          sx={{
            display: 'flex',
            gap: 3,
            order: { xs: 1, md: 2 },

            [theme.breakpoints.up('md')]: {
              ml: 'auto'
            }
          }}
        >
          {policyLinks.map(({ label, to }) => (
            <MUILink
              key={label}
              component={Link}
              to={to}
              underline="none"
              sx={{
                fontSize: '14px',
                color: '#A8A8A8'
              }}
            >
              {label}
            </MUILink>
          ))}
        </Box>
      </Box>
    </Box>
  );
}
